import { FocusEvent, useEffect } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  ariaDescribedByIds,
  descriptionId,
  getTemplate,
  labelValue,
  schemaRequiresTrueValue,
  FormContextType,
  RJSFSchema,
  StrictRJSFSchema,
  WidgetProps
} from '@rjsf/utils';
import BoolIcon from '../../BoolIcon';
import { getNestedValue } from '@/_utils/utils';

/** The `CheckBoxWidget` is a widget for rendering boolean properties.
 *  It is typically used to represent a boolean.
 *
 * @param props - The `WidgetProps` for this component
 */
export default function CheckboxWidget<T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
  props: WidgetProps<T, S, F>
) {
  const {
    schema,
    id,
    value,
    disabled,
    readonly,
    label = '',
    hideLabel,
    autofocus,
    onChange,
    onBlur,
    onFocus,
    registry,
    options,
    uiSchema,
    extraData,
    formData
  } = props;
  const DescriptionFieldTemplate = getTemplate<'DescriptionFieldTemplate', T, S, F>('DescriptionFieldTemplate', registry, options);
  // Because an unchecked checkbox will cause html5 validation to fail, only add
  // the "required" attribute if the field value must be "true", due to the
  // "const" or "enum" keywords
  const required = schemaRequiresTrueValue<S>(schema);

  useEffect(() => {
    // putting a timeout in here to avoid a race condition with values coming from template_inputs in order placement
    setTimeout(() => {
      if (!schema.format_options || value !== undefined) return undefined;
      schema.format_options.forEach((option: any) => {
        if (option.context && option.context !== 'local') {
          if (!extraData?.[option.context] || extraData[option.context] === undefined) return;
          onChange(getNestedValue(option.default_value, extraData[option.context]));
        } else {
          onChange(getNestedValue(option.default_value, formData));
        }
      });
    }, 500);
  }, []);

  const _onChange = (_: any, checked: boolean) => onChange(checked);
  const _onBlur = ({ target: { value } }: FocusEvent<HTMLButtonElement>) => onBlur(id, value);
  const _onFocus = ({ target: { value } }: FocusEvent<HTMLButtonElement>) => onFocus(id, value);
  const description = options.description ?? schema.description;

  if (readonly)
    return (
      <BoolIcon
        sx={{ mb: 1.5 }}
        label={String(labelValue(label, hideLabel, false))}
        checked={typeof value === 'undefined' ? false : Boolean(value)}
      />
    );
  return (
    <>
      {!hideLabel && !!description && (
        <DescriptionFieldTemplate id={descriptionId<T>(id)} description={description} schema={schema} uiSchema={uiSchema} registry={registry} />
      )}
      <FormControlLabel
        control={
          <Checkbox
            id={id}
            name={id}
            checked={typeof value === 'undefined' ? false : Boolean(value)}
            required={required}
            disabled={disabled || readonly}
            autoFocus={autofocus}
            onChange={_onChange}
            onBlur={_onBlur}
            onFocus={_onFocus}
            aria-describedby={ariaDescribedByIds<T>(id)}
          />
        }
        label={labelValue(label, hideLabel, false)}
      />
    </>
  );
}
